import { History, RotateCcw } from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Callout, Spinner } from "@/components/ui/feedback";
import type { FlowSummary } from "@/lib/queries-intake";

/**
 * Every version of one tree, and rolling one back (ACL-338).
 *
 * A rollback is a draft, not a publish: the chosen version's steps come back to be reviewed and
 * published through the ordinary path. So this says what will happen before it happens — which
 * version is live, what number the rollback will take, and that nothing changes for a lead
 * mid-call either way.
 */
export function VersionHistory({
  open,
  onOpenChange,
  versions,
  loading,
  treeTitle,
  treeActive,
  hasDraft,
  restoring,
  onRestore,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versions: FlowSummary[];
  loading: boolean;
  treeTitle: string;
  /** Whether this tree is the one running; publishing on another changes nothing a lead walks. */
  treeActive: boolean;
  hasDraft: boolean;
  restoring: boolean;
  onRestore: (flow: FlowSummary) => void;
}) {
  const [confirming, setConfirming] = React.useState<FlowSummary | null>(null);
  const next = Math.max(0, ...versions.map((one) => one.version)) + 1;

  React.useEffect(() => {
    if (!open) setConfirming(null);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{treeTitle} · versions</DialogTitle>
          <DialogDescription>
            Every version this tree has had. Rolling one back brings its steps into a new draft to
            review; nothing goes live until you publish it.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {!treeActive ? (
            <Callout tone="info">
              This tree is not the one running, so publishing here changes nothing any lead walks.
            </Callout>
          ) : null}

          {loading ? (
            <Spinner />
          ) : versions.length === 0 ? (
            <p className="text-sm text-text-muted">No versions yet.</p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {versions.map((one) => (
                <li key={one.id} className="flex items-center justify-between gap-3 p-3">
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 text-sm font-medium">
                      v{one.version}
                      {one.status === "published" ? (
                        <Badge tone="success" size="sm">
                          Live
                        </Badge>
                      ) : one.status === "draft" ? (
                        <Badge tone="accent" size="sm">
                          Draft
                        </Badge>
                      ) : (
                        <Badge tone="outline" size="sm">
                          Retired
                        </Badge>
                      )}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-text-muted">
                      {/* After a rollback the number no longer names the graph, so it says where
                          the steps came from rather than leaving it to be guessed. */}
                      {one.restored_from ? `Rolled back from v${one.restored_from} · ` : ""}
                      {one.published_at
                        ? `first went live ${new Date(one.published_at).toLocaleDateString()}`
                        : "never published"}
                    </p>
                  </div>
                  {one.status === "draft" ? null : (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={hasDraft || restoring}
                      title={
                        hasDraft
                          ? "This tree already has a draft. Publish or discard it first."
                          : undefined
                      }
                      onClick={() => setConfirming(one)}
                    >
                      <RotateCcw className="mr-1 size-3.5" />
                      Roll back
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>

      {confirming ? (
        <Dialog open onOpenChange={(next) => !next && setConfirming(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Roll back to v{confirming.version}?</DialogTitle>
              <DialogDescription>
                Its steps come back as a new draft, v{next}. Nothing goes live until you publish
                it, and a lead already mid-intake keeps the version they started on to the end of
                their call.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirming(null)}>
                Cancel
              </Button>
              <Button
                loading={restoring}
                onClick={() => {
                  onRestore(confirming);
                  setConfirming(null);
                }}
              >
                Roll back
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </Dialog>
  );
}

/** The button that opens the history, for the page header. */
export function VersionHistoryButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="ghost" size="sm" onClick={onClick} aria-label="Version history">
      <History className="size-4" />
    </Button>
  );
}
